/**
 * QueueTabs component.
 *
 * Tab navigation for queue views using the unified attention model.
 * "All" shows all attention items. "Mine" filters to items assigned
 * to the current operator's tag. "History" shows resolved tool approvals.
 */

import type { CSSProperties } from 'react'
import { NavLink } from 'react-router-dom'
import type { AttentionQueueTab, QueueView } from '../types'
import { ASSIGNMENT_ENABLED } from '../featureFlags'

/**
 * Tab configuration for queue views.
 *
 * Hrefs are built per-render from the current `view` (attention | responses)
 * so each top-level view has its own All/Mine/History sub-tabs under
 * /queue/{view}/{tab}.
 */
interface TabConfig {
  tab: AttentionQueueTab
  label: string
  /** URL segment appended after /queue/{view}/. */
  pathSegment: string
}

// `mine` is included only when the backend supports operator assignment
// (see featureFlags.ts). With assignment disabled there's nothing to
// filter the tab against, so hiding it avoids a confusing empty view.
const ALL_TABS: TabConfig[] = [
  { tab: 'all', label: 'All', pathSegment: 'all' },
  { tab: 'mine', label: 'Mine', pathSegment: 'my' },
  { tab: 'history', label: 'History', pathSegment: 'history' },
]

const TABS: TabConfig[] = ALL_TABS.filter((t) => t.tab !== 'mine' || ASSIGNMENT_ENABLED)

export interface QueueTabsProps {
  /** Which top-level queue view these sub-tabs belong to. Drives href prefix. */
  view: QueueView
  /** Current attention item count for badge display (this view only). */
  count: number | null
  /** Count of items assigned to current operator (this view only). */
  mineCount?: number
}

/**
 * Tab navigation for queue views.
 *
 * Renders the primary queue filters as navigation tabs with live counts.
 * Hrefs are namespaced under the current top-level view (attention or
 * responses) so switching All/Mine/History never crosses views.
 */
export function QueueTabs({ view, count, mineCount }: QueueTabsProps) {
  return (
    <nav role="tablist" aria-label="Queue filters" style={styles.nav}>
      {TABS.map((tab) => {
        const badgeCount =
          tab.tab === 'all'
            ? count
            : tab.tab === 'mine'
              ? (mineCount ?? null)
              : null
        const href = `/queue/${view}/${tab.pathSegment}`

        return (
          <NavLink
            key={tab.tab}
            to={href}
            role="tab"
            style={styles.tabLink}
            className={({ isActive }) => (isActive ? 'queue-tab-active' : 'queue-tab')}
          >
            {() => (
              <>
                <span>{tab.label}</span>
                {badgeCount !== null && badgeCount > 0 && (
                  <span style={styles.countBadge}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        )
      })}
    </nav>
  )
}

const styles: Record<string, CSSProperties> = {
  nav: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    height: '36px',
    padding: '3px',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
  },
  tabLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '13px',
    fontWeight: 500,
    textDecoration: 'none',
    color: 'var(--text-muted)',
    transition:
      'background-color var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast)',
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '18px',
    height: '18px',
    padding: '0 5px',
    borderRadius: '9px',
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: 1,
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'var(--text-secondary)',
  },
}

export default QueueTabs
